import { EffectiveAttributeGroup, EffectiveAttribute } from '@/services/productService';
import { stripHtml } from '@/utils/textUtils';

// Add import for the Attribute interface
export interface Attribute {
  id: number;
  code: string;
  label: string;
  data_type: string;
  is_localisable: boolean;
  is_scopable: boolean;
}

export interface AttributeMetadata {
  id: number;
  code: string;
  label: string;
  data_type: string;
  is_localisable: boolean;
  is_scopable: boolean;
  default_unit?: string | null;
  allowed_units?: string[];
  default_currency?: string | null;
  validation_rule?: string | null;
  allowed_html_tags?: string[];
  allowed_file_types?: string[];
  max_file_size?: number | null;
}

export interface AttributeExportField {
  field: string;
  header: string;
  attributeCode: string;
  attributeMetadata: AttributeMetadata;
}

export interface EnrichedProduct {
  [key: string]: any;
  attributeValues: Map<string, AttributeValueData>;
}

export interface AttributeValueData {
  value: any;
  dataType: string;
  hasRecord: boolean;
  locale?: string | null;
  channel?: string | null;
}

/**
 * Create attribute export fields from organization-wide attributes (all attributes)
 * This is used for organization-wide attribute discovery instead of product-specific discovery
 */
export function createAttributeExportFieldsFromAttributes(
  attributes: Attribute[]
): AttributeExportField[] {
  const attributeFields: AttributeExportField[] = [];
  const seenCodes = new Set<string>();

  attributes.forEach(attr => {
    // Avoid duplicates (shouldn't happen with organization attributes, but safety check)
    if (seenCodes.has(attr.code)) return;
    seenCodes.add(attr.code);

    attributeFields.push({
      field: `attr_${attr.code}`,
      header: attr.label,
      attributeCode: attr.code,
      attributeMetadata: {
        id: attr.id,
        code: attr.code,
        label: attr.label,
        data_type: attr.data_type,
        is_localisable: attr.is_localisable,
        is_scopable: attr.is_scopable,
        // Set defaults for optional fields since organization attributes don't have these product-specific details
        default_unit: null,
        allowed_units: [],
        default_currency: null,
        validation_rule: null,
        allowed_html_tags: [],
        allowed_file_types: [],
        max_file_size: null,
      },
    });
  });

  // Sort by label for consistent ordering
  return attributeFields.sort((a, b) => a.header.localeCompare(b.header));
}

/**
 * Create attribute export fields from effective attribute groups
 */
export function createAttributeExportFields(
  effectiveAttributeGroups: EffectiveAttributeGroup[]
): AttributeExportField[] {
  const attributeFields: AttributeExportField[] = [];
  const seenCodes = new Set<string>();

  effectiveAttributeGroups.forEach(group => {
    group.attributes.forEach(attr => {
      // Avoid duplicates (same attribute might appear in multiple groups)
      if (seenCodes.has(attr.code)) return;
      seenCodes.add(attr.code);

      attributeFields.push({
        field: `attr_${attr.code}`,
        header: attr.label,
        attributeCode: attr.code,
        attributeMetadata: {
          id: attr.id,
          code: attr.code,
          label: attr.label,
          data_type: attr.data_type,
          is_localisable: attr.is_localisable,
          is_scopable: attr.is_scopable,
          default_unit: attr.default_unit,
          allowed_units: attr.allowed_units,
          default_currency: attr.default_currency,
          validation_rule: attr.validation_rule,
          allowed_html_tags: attr.allowed_html_tags,
          allowed_file_types: attr.allowed_file_types,
          max_file_size: attr.max_file_size,
        },
      });
    });
  });

  // Sort by label for consistent ordering
  return attributeFields.sort((a, b) => a.header.localeCompare(b.header));
}

/**
 * Create attribute value lookup map from effective attributes
 */
export function createAttributeValueMap(
  effectiveAttributeGroups: EffectiveAttributeGroup[]
): Map<string, AttributeValueData> {
  const attributeValueMap = new Map<string, AttributeValueData>();

  effectiveAttributeGroups.forEach(group => {
    group.attributes.forEach(attr => {
      attributeValueMap.set(attr.code, {
        value: attr.value,
        dataType: attr.data_type,
        hasRecord: attr.has_record,
        locale: attr.locale,
        channel: attr.channel,
      });
    });
  });

  return attributeValueMap;
}

/**
 * Format attribute value based on its data type and metadata
 */
export function formatAttributeValue(value: any, attr: AttributeMetadata, i18n: any): string {
  if (value === null || value === undefined) return '';

  switch (attr.data_type) {
    case 'text':
    case 'email':
    case 'phone':
    case 'url':
      return String(value);

    case 'rich_text':
      return stripHtml(String(value));

    case 'number':
      return typeof value === 'number' ? value.toString() : String(value);

    case 'boolean':
      if (typeof value === 'boolean') {
        return value
          ? i18n.getExcelStatusText('yes') || 'Yes'
          : i18n.getExcelStatusText('no') || 'No';
      }
      // Handle string representations
      const strValue = String(value).toLowerCase();
      if (strValue === 'true' || strValue === '1' || strValue === 'yes') {
        return i18n.getExcelStatusText('yes') || 'Yes';
      }
      if (strValue === 'false' || strValue === '0' || strValue === 'no') {
        return i18n.getExcelStatusText('no') || 'No';
      }
      return String(value);

    case 'date':
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return String(value);
        return date.toLocaleDateString();
      } catch {
        return String(value);
      }

    case 'measurement':
      if (typeof value === 'object' && value !== null) {
        if (value.amount !== undefined && value.unit) {
          return `${value.amount} ${value.unit}`;
        }
        if (value.value !== undefined && value.unit) {
          return `${value.value} ${value.unit}`;
        }
      }
      return String(value);

    case 'price':
      if (typeof value === 'object' && value !== null) {
        if (value.amount !== undefined && value.currency) {
          return `${value.amount} ${value.currency}`;
        }
        if (value.value !== undefined && value.currency) {
          return `${value.value} ${value.currency}`;
        }
      }
      return String(value);

    case 'select':
      // Handle select options (may be arrays for multi-select)
      if (Array.isArray(value)) {
        return value
          .map(item => {
            if (typeof item === 'object' && item?.label) return item.label;
            if (typeof item === 'object' && item?.name) return item.name;
            return String(item);
          })
          .join(', ');
      }
      if (typeof value === 'object' && value?.label) return value.label;
      if (typeof value === 'object' && value?.name) return value.name;
      return String(value);

    case 'media':
      // For media, export the URL or filename
      if (typeof value === 'object' && value !== null) {
        if (value.url) return value.url;
        if (value.file_url) return value.file_url;
        if (value.filename) return value.filename;
        if (value.name) return value.name;
      }
      return String(value);

    default:
      // Handle arrays generically
      if (Array.isArray(value)) {
        return value
          .map(item => {
            if (typeof item === 'object' && item?.name) return item.name;
            if (typeof item === 'object' && item?.label) return item.label;
            return String(item);
          })
          .join(', ');
      }

      // Handle objects generically
      if (typeof value === 'object' && value !== null) {
        if (value.name) return String(value.name);
        if (value.label) return String(value.label);
        if (value.value) return String(value.value);
        return JSON.stringify(value);
      }

      return String(value);
  }
}

/**
 * Calculate column width based on attribute data type and content
 */
export function calculateAttributeColumnWidth(attr: AttributeMetadata): { wch: number } {
  const baseWidth = Math.max(attr.label.length, 10); // Minimum based on header length

  switch (attr.data_type) {
    case 'text':
    case 'rich_text':
      return { wch: Math.min(baseWidth + 10, 50) }; // Max 50 for text fields

    case 'number':
    case 'boolean':
      return { wch: Math.max(baseWidth, 12) };

    case 'date':
      return { wch: Math.max(baseWidth, 18) }; // Accommodate date format

    case 'measurement':
    case 'price':
      return { wch: Math.max(baseWidth, 15) }; // Accommodate value + unit/currency

    case 'select':
      return { wch: Math.min(baseWidth + 5, 30) }; // Moderate width for selections

    case 'url':
    case 'media':
      return { wch: Math.min(baseWidth + 15, 40) }; // Wider for URLs

    case 'email':
    case 'phone':
      return { wch: Math.max(baseWidth, 20) };

    default:
      return { wch: Math.max(baseWidth, 15) };
  }
}

/**
 * Deduplicate attributes across multiple effective attribute groups
 */
export function deduplicateAttributesAcrossProducts(
  allProductAttributes: EffectiveAttributeGroup[][]
): EffectiveAttribute[] {
  const attributeMap = new Map<string, EffectiveAttribute>();

  allProductAttributes.forEach(productGroups => {
    productGroups.forEach(group => {
      group.attributes.forEach(attr => {
        if (!attributeMap.has(attr.code)) {
          attributeMap.set(attr.code, attr);
        }
      });
    });
  });

  return Array.from(attributeMap.values()).sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Excel column limit management
 */
export const EXCEL_LIMITS = {
  MAX_COLUMNS: 16384,
  MAX_ROWS: 1048576,
  WARN_THRESHOLD_COLUMNS: 1000,
  WARN_THRESHOLD_ROWS: 10000,
} as const;

/**
 * Check if export would exceed Excel limits
 */
export function checkExcelLimits(
  baseColumnCount: number,
  attributeColumnCount: number,
  rowCount: number
): {
  isValid: boolean;
  warnings: string[];
  errors: string[];
} {
  const warnings: string[] = [];
  const errors: string[] = [];

  const totalColumns = baseColumnCount + attributeColumnCount;

  if (totalColumns > EXCEL_LIMITS.MAX_COLUMNS) {
    errors.push(`Too many columns: ${totalColumns} (max: ${EXCEL_LIMITS.MAX_COLUMNS})`);
  } else if (totalColumns > EXCEL_LIMITS.WARN_THRESHOLD_COLUMNS) {
    warnings.push(`Large number of columns: ${totalColumns}. Export may be slow.`);
  }

  if (rowCount > EXCEL_LIMITS.MAX_ROWS) {
    errors.push(`Too many rows: ${rowCount} (max: ${EXCEL_LIMITS.MAX_ROWS})`);
  } else if (rowCount > EXCEL_LIMITS.WARN_THRESHOLD_ROWS) {
    warnings.push(`Large number of rows: ${rowCount}. Export may be slow.`);
  }

  return {
    isValid: errors.length === 0,
    warnings,
    errors,
  };
}
